import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Phone, Calendar, Clock, User, MapPin, CheckCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { companyInfo } from '../../data/mock';

const AirConditioningServiceRosevilleCA = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#FDFCF7]" data-testid="blog-post-ac-service">
      {/* Navigation Bar */}
      <div className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/blog')}
              data-testid="back-to-blog-btn"
              className="flex items-center gap-2 text-[#622B62] hover:text-[#DA9C3A] transition-colors font-medium"
            >
              <ArrowLeft size={20} />
              Back to Blog
            </button>
          </div>
          <a href={`tel:${companyInfo.phone}`}>
            <Button className="bg-[#DA9C3A] hover:bg-[#C48A2A] text-white font-semibold px-5 py-2 rounded-full">
              <Phone size={16} className="mr-2" />
              {companyInfo.phone}
            </Button>
          </a>
        </div>
      </div>

      {/* Article Hero */}
      <div className="bg-[#622B62] text-white py-16 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#7A3D7A] rounded-full -translate-y-1/2 translate-x-1/2 opacity-50" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#4A1F4A] rounded-full translate-y-1/2 -translate-x-1/2 opacity-50" />
        <div className="max-w-3xl mx-auto px-4 relative z-10">
          <span className="inline-block bg-[#DA9C3A] text-white text-xs font-bold px-3 py-1 rounded-full mb-4">Air Conditioning</span>
          <h1 className="romano-heading text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 leading-tight" data-testid="article-heading" data-speakable="true">
            Air Conditioning Service Roseville CA: Complete Guide to Keeping Your Home Cool
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-white/80">
            <span className="flex items-center gap-1"><User size={14} /> Juliano Romano</span>
            <span className="flex items-center gap-1"><Calendar size={14} /> June 2, 2026</span>
            <span className="flex items-center gap-1"><Clock size={14} /> 8 min read</span>
            <span className="flex items-center gap-1"><MapPin size={14} /> Roseville, CA</span>
          </div>
        </div>
      </div>

      {/* Article Content */}
      <article className="max-w-3xl mx-auto px-4 py-12">
        <div className="prose prose-lg max-w-none">

          {/* Intro - AEO optimized for direct answer */}
          <div className="bg-white rounded-2xl border border-gray-200 p-8 mb-10 shadow-sm" data-speakable="true">
            <p className="text-gray-700 text-lg leading-relaxed mb-0">
              <strong>Air conditioning service in Roseville CA</strong> is essential for keeping your home comfortable during our region's hot summers, when temperatures routinely exceed 100°F. Professional AC service includes routine maintenance, system inspections, refrigerant checks, and emergency repairs — all designed to keep your cooling system running efficiently and prevent costly breakdowns. Romano Heating & Cooling has been providing expert air conditioning service to Roseville CA homeowners since 2005.
            </p>
          </div>

          {/* Table of Contents */}
          <div className="bg-[#622B62]/5 rounded-2xl p-6 mb-10">
            <h2 className="romano-heading text-xl font-bold text-[#622B62] mb-4">In This Guide</h2>
            <ul className="space-y-2">
              {[
                'Why Regular Air Conditioning Service Matters in Roseville CA',
                'What Professional AC Service Includes',
                'Signs Your Air Conditioner Needs Service',
                'How Often Should You Schedule AC Service in Roseville?',
                'Choosing the Right HVAC Contractor in Roseville CA',
                'Energy Efficiency Tips for Roseville Homeowners',
                'Emergency Air Conditioning Repair in Roseville CA',
                'Service Areas Near Roseville CA',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-gray-700">
                  <span className="text-[#DA9C3A] font-bold">{i + 1}.</span> {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Section 1 */}
          <h2 className="romano-heading text-2xl font-bold text-[#622B62] mt-12 mb-4" data-speakable="true">
            Why Regular Air Conditioning Service Matters in Roseville CA
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Roseville, California sits in the Sacramento Valley where summer temperatures regularly hit 100°F to 110°F between June and September. That puts enormous strain on residential air conditioning systems. Without regular professional air conditioning service, your AC unit works harder, consumes more energy, and faces a significantly higher risk of failure — often at the worst possible time.
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">
            According to the U.S. Department of Energy, regular air conditioning maintenance can reduce energy consumption by 15% to 25%. For Roseville homeowners running their AC systems 6+ months per year, that translates to hundreds of dollars in annual savings.
          </p>
          <div className="bg-[#DA9C3A]/10 border-l-4 border-[#DA9C3A] p-5 rounded-r-xl my-6">
            <p className="text-gray-800 font-medium mb-0">
              Pro Tip: The best time to schedule air conditioning service in Roseville CA is during spring (March–May), before peak cooling season begins. This ensures your system is ready for the extreme summer heat.
            </p>
          </div>

          {/* Section 2 */}
          <h2 className="romano-heading text-2xl font-bold text-[#622B62] mt-12 mb-4" data-speakable="true">
            What Professional Air Conditioning Service Includes
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            A comprehensive air conditioning service in Roseville CA from Romano Heating & Cooling covers every component of your cooling system:
          </p>
          <div className="grid sm:grid-cols-2 gap-3 my-6">
            {[
              'Complete system inspection',
              'Condenser and evaporator coil cleaning',
              'Refrigerant level check and recharge',
              'Thermostat calibration and testing',
              'Electrical connection inspection',
              'Lubrication of all moving parts',
              'Air filter replacement',
              'Condensate drain line clearing',
              'System performance test',
              'Safety controls verification',
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-2">
                <CheckCircle size={18} className="text-[#DA9C3A] mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>

          {/* Section 3 */}
          <h2 className="romano-heading text-2xl font-bold text-[#622B62] mt-12 mb-4" data-speakable="true">
            Signs Your Air Conditioner Needs Service in Roseville CA
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Roseville homeowners should watch for these warning signs that indicate your air conditioning system needs professional service:
          </p>
          <ul className="space-y-3 my-6">
            {[
              { sign: 'Weak or reduced airflow', detail: 'Could indicate a clogged filter, duct issue, or failing compressor.' },
              { sign: 'Warm air coming from vents', detail: 'May signal low refrigerant, compressor problems, or thermostat malfunction.' },
              { sign: 'Unusual noises', detail: 'Grinding, squealing, or banging sounds often indicate mechanical issues requiring immediate attention.' },
              { sign: 'Strange odors', detail: 'Musty smells suggest mold in ducts; burning odors could indicate electrical problems.' },
              { sign: 'Increased energy bills', detail: 'A sudden spike in cooling costs usually means your system is working harder than it should.' },
              { sign: 'Frequent cycling', detail: 'Short cycling (turning on and off rapidly) stresses components and reduces efficiency.' },
              { sign: 'Excess humidity indoors', detail: 'Your AC should moderate humidity levels. If your home feels sticky, the system may need service.' },
            ].map((item, i) => (
              <li key={i} className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
                <span className="font-bold text-[#622B62]">{item.sign}:</span>{' '}
                <span className="text-gray-600">{item.detail}</span>
              </li>
            ))}
          </ul>

          {/* Section 4 */}
          <h2 className="romano-heading text-2xl font-bold text-[#622B62] mt-12 mb-4" data-speakable="true">
            How Often Should You Schedule AC Service in Roseville?
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            For homes in Roseville CA, we recommend scheduling professional air conditioning service <strong>at least once per year</strong>, ideally in spring before temperatures climb. However, given our region's extended cooling season (April through October), many Roseville homeowners benefit from <strong>twice-yearly service</strong> — once in spring for AC and once in fall for heating.
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">
            Homes with older systems (10+ years), pets, or family members with allergies or respiratory conditions may benefit from more frequent service. Romano Heating & Cooling offers <Link to="/maintenance-plans" className="text-[#DA9C3A] font-semibold hover:underline">affordable maintenance plans</Link> starting at just $9.99/month that include scheduled service visits, priority scheduling, and repair discounts.
          </p>

          {/* Section 5 */}
          <h2 className="romano-heading text-2xl font-bold text-[#622B62] mt-12 mb-4" data-speakable="true">
            Choosing the Right HVAC Contractor for Air Conditioning Service in Roseville CA
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Not all air conditioning service providers in Roseville CA are equal. When choosing an HVAC contractor, look for:
          </p>
          <ul className="space-y-3 my-6">
            {[
              'Valid California contractor\'s license (Romano: LIC #1077499)',
              'Proper insurance and bonding',
              'Positive reviews from local Roseville customers',
              'Transparent, upfront pricing with no hidden fees',
              'Experience with your specific AC brand and model',
              '24/7 emergency service availability',
              'Written warranties on parts and labor',
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-gray-700">
                <CheckCircle size={18} className="text-[#DA9C3A] mt-0.5 flex-shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>

          {/* Section 6 */}
          <h2 className="romano-heading text-2xl font-bold text-[#622B62] mt-12 mb-4" data-speakable="true">
            Energy Efficiency Tips for Roseville Homeowners
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Between professional air conditioning service visits, Roseville CA homeowners can take these steps to maximize efficiency and comfort:
          </p>
          <div className="grid gap-4 my-6">
            {[
              { tip: 'Change air filters monthly', desc: 'During peak cooling season in Roseville, check filters every 30 days. A clogged filter forces your AC to work harder.' },
              { tip: 'Set your thermostat to 78°F when home', desc: 'The DOE recommends 78°F as the ideal balance of comfort and efficiency. Use a programmable or smart thermostat to raise it when you\'re away.' },
              { tip: 'Seal air leaks', desc: 'Check windows, doors, and ductwork for leaks. Even small gaps can let hot Sacramento Valley air into your home.' },
              { tip: 'Use ceiling fans', desc: 'Ceiling fans create a wind-chill effect that lets you set your thermostat 4°F higher without reducing comfort.' },
              { tip: 'Keep vents clear', desc: 'Ensure furniture, curtains, and rugs aren\'t blocking supply or return vents.' },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
                <h4 className="font-bold text-[#622B62] mb-1">{item.tip}</h4>
                <p className="text-gray-600 text-sm mb-0">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Section 7 */}
          <h2 className="romano-heading text-2xl font-bold text-[#622B62] mt-12 mb-4" data-speakable="true">
            Emergency Air Conditioning Repair in Roseville CA
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            When your AC fails during a Roseville heat wave, you need help fast. Romano Heating & Cooling offers <strong>24/7 emergency air conditioning repair</strong> for Roseville CA and all surrounding communities. Our technicians carry common replacement parts on their trucks, allowing us to complete most emergency repairs in a single visit.
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">
            Don't wait for a complete system failure. If you notice any of the warning signs above, scheduling preventive air conditioning service now can save you from an expensive emergency repair later.
          </p>

          {/* Section 8 - GEO */}
          <h2 className="romano-heading text-2xl font-bold text-[#622B62] mt-12 mb-4" data-speakable="true">
            Air Conditioning Service Areas Near Roseville CA
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Romano Heating & Cooling provides professional air conditioning service throughout the greater Roseville CA area. In addition to Roseville, we serve:
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 my-6">
            {[
              'Rocklin, CA',
              'Folsom, CA',
              'Granite Bay, CA',
              'Citrus Heights, CA',
              'Lincoln, CA',
              'Loomis, CA',
              'Auburn, CA',
              'El Dorado Hills, CA',
              'Orangevale, CA',
              'Fair Oaks, CA',
              'Carmichael, CA',
              'Sacramento, CA',
            ].map((city, i) => (
              <div key={i} className="flex items-center gap-2 text-gray-700">
                <MapPin size={14} className="text-[#DA9C3A] flex-shrink-0" />
                <span className="text-sm">{city}</span>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-br from-[#622B62] to-[#4A1F4A] rounded-3xl p-10 text-center text-white relative overflow-hidden my-12">
            <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="relative z-10">
              <h3 className="romano-heading text-2xl sm:text-3xl font-bold mb-4">
                Schedule Your Air Conditioning Service Today
              </h3>
              <p className="text-white/90 mb-8 max-w-xl mx-auto">
                Don't wait for a breakdown. Contact Romano Heating & Cooling for professional air conditioning service in Roseville CA. Free estimates, upfront pricing, and satisfaction guaranteed.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="tel:9162749021">
                  <Button className="bg-[#DA9C3A] hover:bg-[#C48A2A] text-white font-bold px-10 py-6 text-lg rounded-full shadow-xl">
                    <Phone size={22} className="mr-2" />
                    Call (916) 274-9021
                  </Button>
                </a>
                <Button
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-[#622B62] font-bold px-10 py-6 text-lg rounded-full"
                  onClick={() => navigate('/#contact')}
                >
                  Get a Free Estimate
                </Button>
              </div>
            </div>
          </div>

          {/* Author Box */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 flex items-start gap-5 mt-12">
            <img
              src={companyInfo.logo}
              alt="Romano Heating & Cooling"
              className="w-16 h-16 rounded-lg object-contain flex-shrink-0"
            />
            <div>
              <h4 className="font-bold text-[#622B62]">Juliano Romano</h4>
              <p className="text-sm text-gray-500 mb-2">Owner, Romano Heating & Cooling</p>
              <p className="text-gray-600 text-sm">
                With over 20 years of experience in HVAC, Juliano and his team have been providing trusted air conditioning service to Roseville CA and surrounding communities since 2005. Licensed contractor #1077499.
              </p>
            </div>
          </div>

        </div>
      </article>

      {/* Footer */}
      <div className="bg-[#622B62] text-white py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img src={companyInfo.logo} alt="Romano Heating & Cooling" className="h-24 w-24 object-contain rounded-lg" />
            <div className="text-left">
              <h3 className="font-bold romano-heading">Romano Heating & Cooling</h3>
              <p className="text-white/70 text-sm">1316 Church Street, Roseville, CA 95678</p>
            </div>
          </div>
          <p className="text-[#DA9C3A] font-semibold mb-2">(916) 274-9021</p>
          <p className="text-white/60 text-sm">&copy; {new Date().getFullYear()} Romano Heating and Cooling. All Rights Reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default AirConditioningServiceRosevilleCA;
